import CloseIcon from "@mui/icons-material/Close"
import { IconButton } from "@mui/material"
import {
  ChangeEvent,
  Dispatch,
  DragEvent,
  SetStateAction,
  useRef,
  useState,
} from "react"

import { create } from "src/helpers/bem"

import { useTranslation } from "src/hooks/translation"

import styles from "./AuthorizationRequestFileUpload.module.scss"

const bem = create(styles, "AuthorizationRequestFileUpload")

const maxFiles = 5
const maxFileSize = 5242880
const acceptedTypes = ".pdf, .jpg, .jpeg, .png"

type AuthorizationRequestFileUploadProps = {
  files: File[]
  setFiles: Dispatch<SetStateAction<File[]>>
}

export const AuthorizationRequestFileUpload = ({
  files,
  setFiles,
}: AuthorizationRequestFileUploadProps) => {
  const { messages } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)
  const [inputKey, setInputKey] = useState(Math.random())

  const maxFilesReached = files?.length ? maxFiles <= files?.length : false
  const modifier = {
    "is-active": dragActive,
    "is-disabled": maxFilesReached,
  }
  const m =
    messages.pages.account.overview.profile.incidents.createDialog
      .authorizationRequestFields.fileUpload

  const fileSizeToDisplay = (fileSize: number) => {
    const k = 1024 // bytes
    const i = 2 // represents MB
    return (fileSize / Math.pow(k, i)).toFixed(0)
  }

  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setInputKey(Math.random())
    setDragActive(false)
    const newFiles = Array.from(e.dataTransfer.files)
    setFiles((prev) => [...prev, ...newFiles])
  }

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    setInputKey(Math.random())

    if (e.target.files) {
      const newFiles = Array.from(e.target.files)
      setFiles((prev) => [...prev, ...newFiles])
    }
  }

  const handleDeleteFile = async (file: File) => {
    setFiles((prev) => prev.filter((f) => f.name !== file.name))
  }

  const handleButtonClick = () => {
    inputRef?.current?.click()
  }

  return (
    <div className={bem()} onDragEnter={handleDrag}>
      <span className={bem("title")}>
        {
          messages.pages.account.overview.webshop.form.clientLocation.fields
            .businessCertificate
        }
      </span>

      <input
        key={inputKey}
        ref={inputRef}
        accept={acceptedTypes}
        className={bem("input")}
        disabled={maxFilesReached}
        id={`file-upload`}
        multiple={true}
        type="file"
        onChange={handleChange}
      />
      <label className={bem("label", modifier)} htmlFor={`file-upload`}>
        <div>
          <p className={bem("description")}>
            {maxFilesReached ? m.maxFilesReached : m.description}
          </p>
          <button
            className={bem("button", modifier)}
            disabled={maxFilesReached}
            onClick={handleButtonClick}
          >
            {m.button}
          </button>
        </div>
        {maxFiles && (
          <span className={bem("fileLength")}>
            {files.length} / {maxFiles}
          </span>
        )}
      </label>
      {dragActive && !maxFilesReached && (
        <div
          className={bem("drag")}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        />
      )}
      <div className={bem("fileRestrictions")}>
        <p>
          {`${m.maxFileSizePerFile1} 
            ${fileSizeToDisplay(maxFileSize)}
            ${m.maxFileSizePerFile2}`}
        </p>
        <p>{`${m.supportedFileType1} 
            ${acceptedTypes}`}</p>
      </div>
      {files.length > 0 && (
        <div className={bem("file__wrapper")}>
          {files.flatMap((file, i) => (
            <div key={`${file.name}-file-${i}`} className={bem("file")}>
              <span className={bem("file__name")}>{file?.name}</span>
              <div>
                <span className={bem("file__name")}>
                  {file?.size
                    ? `${Math.round(
                        Number(file?.size ?? 0) / Math.pow(1024, 1),
                      )} KB`
                    : "0 KB"}
                </span>
                <IconButton
                  aria-label="close"
                  color="error"
                  size="small"
                  onClick={() => handleDeleteFile(file)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AuthorizationRequestFileUpload
